import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import Header from "@/components/Header";
import {
  Wallet,
  TrendingUp,
  Clock,
  Phone,
  Video,
  Calendar,
  Download,
  ChevronRight,
  Loader2,
  IndianRupee,
  Info,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { getEarningsSummary, getEarningsSessions } from "@/api/earnings";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDateIST = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "orange" }) => {
  const colorClasses = {
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={cn("p-2 rounded-lg", colorClasses[color])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {typeof trend === "number" && (
        <div className="flex items-center gap-1 mt-2">
          <TrendingUp className={cn("h-3 w-3", trend >= 0 ? "text-green-500" : "text-red-500")} />
          <span className={cn("text-xs", trend >= 0 ? "text-green-500" : "text-red-500")}>
            {trend >= 0 ? "+" : ""}{trend}% vs last month
          </span>
        </div>
      )}
    </div>
  );
};

const SessionRow = ({ session }) => {
  const minutes = Math.ceil((session.durationSeconds || 0) / 60);
  const ServiceIcon = session.serviceType === "video" ? Video : Phone;
  const isReversed = !!session.reversed;

  return (
    <div className={cn(
      "flex items-center justify-between py-3 border-b border-gray-100 last:border-0",
      isReversed && "opacity-60"
    )}>
      <div className="flex items-center gap-3">
        <div className={cn(
          "p-2 rounded-full",
          session.isFreeTrial ? "bg-blue-100" : "bg-green-100"
        )}>
          <ServiceIcon className={cn(
            "h-4 w-4",
            session.isFreeTrial ? "text-blue-600" : "text-green-600"
          )} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
            {session.userName || "User"}
            {session.isFreeTrial && (
              <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">FREE TRIAL</span>
            )}
            {isReversed && (
              <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">REVERSED</span>
            )}
          </p>
          <p className="text-xs text-gray-500">
            {formatDateIST(session.date)} · {session.serviceType} · {minutes} min
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={cn(
          "text-sm font-semibold",
          isReversed ? "text-gray-400 line-through" : "text-green-600"
        )}>
          +{formatCurrency(session.netAmount)}
        </p>
        <p className="text-[11px] text-gray-400">
          Gross {formatCurrency(session.grossAmount)} − Commission {formatCurrency(session.commissionAmount)}
        </p>
      </div>
    </div>
  );
};

export default function EarningsPage() {
  const { astrologer } = useAuth();
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [sumRes, sessRes] = await Promise.all([
        getEarningsSummary(astrologer.astrologerId, astrologer.accessToken),
        getEarningsSessions(astrologer.astrologerId, astrologer.accessToken, { page: 1, limit: 20 }),
      ]);
      if (sumRes.status === 200 && sumRes.data) setSummary(sumRes.data);
      else setError(sumRes.error || "Failed to load summary");

      if (sessRes.status === 200 && sessRes.data) setSessions(sessRes.data.sessions || []);
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [astrologer?.astrologerId, astrologer?.accessToken]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <>
        <Header name="Earnings" />
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
        </div>
      </>
    );
  }

  const today = summary?.today || { net: 0, sessionCount: 0 };
  const thisWeek = summary?.thisWeek || { net: 0, sessionCount: 0, seconds: 0 };
  const thisMonth = summary?.thisMonth || { net: 0, sessionCount: 0, seconds: 0 };
  const lifetime = summary?.lifetime || { net: 0, sessionCount: 0 };
  const totalMinutes = Math.floor((thisMonth.seconds || 0) / 60);

  return (
    <>
      <Header name="Earnings" />
      <div className="w-full p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Today"
            value={formatCurrency(today.net)}
            subtitle={`${today.sessionCount || 0} sessions`}
            icon={Calendar}
            color="blue"
          />
          <StatCard
            title="This Month"
            value={formatCurrency(thisMonth.net)}
            trend={summary?.monthDelta}
            icon={IndianRupee}
            color="green"
          />
          <StatCard
            title="Total Minutes"
            value={`${totalMinutes}m`}
            subtitle={`${thisMonth.sessionCount || 0} calls this month`}
            icon={Clock}
            color="blue"
          />
          <StatCard
            title="Rate"
            value={`₹${summary?.ratePerMinute || 0}/min`}
            subtitle={`Commission ${summary?.commissionPercent ?? 30}%`}
            icon={TrendingUp}
            color="purple"
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Sessions drill-down */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-gray-900">Recent Sessions</h2>
              <span className="text-xs text-gray-400">IST</span>
            </div>
            <div className="p-4">
              {sessions.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                  No sessions yet. Your earnings will appear here after your first call.
                </div>
              ) : (
                sessions.map((s) => <SessionRow key={s.sessionId} session={s} />)
              )}
            </div>
          </div>

          {/* Payout Info (coming soon) */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900">Payout</h2>
            </div>
            <div className="p-4 space-y-4">
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600">Lifetime Net Earnings</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {formatCurrency(lifetime.net)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {lifetime.sessionCount || 0} sessions
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payment Method</span>
                  <span className="font-medium text-gray-400">Not set</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payout Cycle</span>
                  <span className="font-medium text-gray-400">Coming soon</span>
                </div>
              </div>

              <button
                disabled
                className="w-full py-2 px-4 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
              >
                Request Payout (Coming Soon)
              </button>

              <div className="pt-4 border-t">
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 cursor-not-allowed"
                >
                  <Download className="h-4 w-4" />
                  Statement (Coming Soon)
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg flex gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-700">
            <strong>How earnings work:</strong> You earn ₹{summary?.ratePerMinute || 0}/minute for every consultation,
            minus a {summary?.commissionPercent ?? 30}% platform commission. Free trial minutes count toward your earnings.
            Automatic payout to your bank is coming soon.
          </p>
        </div>
      </div>
    </>
  );
}
